import {
  Box,
  Card,
  CardContent,
  CardHeader,
  Stack,
  Typography,
} from "@mui/material";
import { Download, Download4Plot } from "../analytics/analytics-types";
import { useTranslation } from "react-i18next";
import ReactApexChart from "react-apexcharts";
import { ApexOptions } from "apexcharts";
import NoDataToBeShown from "../reusable/NoDataToBeShown";
import ErrorComponent from "../reusable/ErrorComponent";
import { Skeleton4Chart } from "./LinkChart";
import BarChartIcon from "@mui/icons-material/BarChart";
import { grey } from "@mui/material/colors";

/* Component showing the amount of total downloads aggregated per day: divided per apps or total apps.
  It takes an array of Download type as input. */
export default function GlobalBarChart(props: {
  title: string;
  downloads: Download[];
  loading: boolean;
  isError: any;
}) {
  const { t } = useTranslation();
  const downloadsByDate: { [date: string]: number } = {};
  const DownloadsForPlot: Download4Plot[] = [];

  /* Data are aggregated by date. */
  props.downloads.forEach((item) => {
    const date = item.timestamp.slice(0, 10);
    if (downloadsByDate[date]) {
      downloadsByDate[date] += 1;
    } else {
      downloadsByDate[date] = 0;
      downloadsByDate[date] += 1;
    }
  });

  /* A new object of Download4Plot type is created starting from downloadByDate */
  Object.entries(downloadsByDate).map((element) => {
    const downloadObject: Download4Plot = { date: "", downloadAmount: 0 };
    downloadObject.date = element[0];
    downloadObject.downloadAmount = element[1];
    DownloadsForPlot.push(downloadObject);

    return DownloadsForPlot;
  });

  const sortedDownloads = DownloadsForPlot?.sort((a, b) =>
    new Date(a.date).getTime() > new Date(b.date).getTime() ? 1 : -1
  );

  const chartData: ApexOptions = {
    chart: {
      type: "bar",
      id: "apexchart-example",
      toolbar: {
        show: true,
        offsetX: 0,
        offsetY: 0,
        tools: {
          download: true,
          selection: true,
          zoom: true,
          zoomin: true,
          zoomout: true,
          pan: true,
          reset: false,
          customIcons: [],
        },
      },
    },
    plotOptions: {
      bar: {
        borderRadius: 4,
        columnWidth: "60%",
      },
    },
    xaxis: {
      title: {
        text: t("Days"),
      },
      categories: sortedDownloads.map((item) => item.date),
    },
    yaxis: {
      title: {
        text: "Downloads",
      },
      floating: false,
      axisBorder: {
        show: false,
      },
      min: 0,
      labels: {
        formatter: function (val) {
          return val.toFixed(0);
        },
      },
    },

    fill: {
      type: "gradient",
      gradient: {
        shade: "light",
        type: "horizontal",
        shadeIntensity: 0.5,
        gradientToColors: undefined, // optional, if not defined - uses the shades of same color in series
        inverseColors: true,
        opacityFrom: 1,
        opacityTo: 1,
        stops: [0, 50, 100],
        // colorStops: []
      },
    },
    legend: {
      // position: '',
      width: 400,
      // position: 'top',
    },
    title: {
      text: props.title,
      floating: false,
      margin: 2,
      align: "center",
      offsetY: 2,
      offsetX: 2,
      style: {
        color: "#444",
      },
    },
    series: [
      {
        name: t("Download amount"),
        type: "bar",
        data: sortedDownloads.map((item) => item.downloadAmount),
      },
    ],
  };

  return (
    <Card
      sx={{
        boxShadow: 5,
        borderRadius: 1.5,
        height: 400,
        justifyContent: "center",
      }}
      style={{ height: "100%" }}
    >
      <CardHeader title={t("Amount of downloads")} />
      <CardContent>
        {" "}
        {props.loading ? (
          <Skeleton4Chart />
        ) : props.downloads.length > 0 ? (
          <ReactApexChart options={chartData} series={chartData.series} />
        ) : !props.isError && props.downloads.length === 0 ? (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              height: "40vh",
              alignItems: "center",
            }}
          >
            <Stack justifyContent="center" alignItems="center">
              <BarChartIcon sx={{ fontSize: 60, color: grey[500] }} />
              <NoDataToBeShown />
              <Typography textAlign="center">
                {t("Share you apps to see your downloads trend.")}
              </Typography>
            </Stack>
          </Box>
        ) : (
          props.isError && <ErrorComponent />
        )}
      </CardContent>
    </Card>
  );
}
